import React, {FC, Fragment, ReactNode} from "react";
import {useRouter} from "next/router";
import cls from "./category.module.scss";
import {IProduct, TranslationsType} from "../../../types/homePageTypes";
import InfiniteContentLayout from "./InfiniteContentLayout";
import BreadcrumbHeader from "../BreadcrumbHeader";
import CustomDropdown from "./CustomDropdown";
import CustomCategorySidebar from "./CustomCategorySidebar";
import CustomWidgetFilters from "./CustomWidgetFilters";
import {ICurrencies} from "../Wrappers/MainLayout";
import {IAttributeValue} from "../../../types/widgetFiltresTypes";
import CustomProductView from "./CustomProductView";
import BuilderSection from "../../builder/BuilderDataRender";
import {useSelector} from "react-redux";
import {IState} from "../../../types/state";
import dynamic from "next/dynamic";
import {optionsHandler} from "./helpers";
import {ParsedUrlQuery} from "node:querystring";

interface IShopPageCategoryProps {
    brandList: Array<IAttributeValue>;
    productsList: IProductList,
    dbName: string;
    domain: string;
    titleCategory: string;
    categoryDescription: string | TrustedHTML;
    selectedRate: ICurrencies;
    translationsData: TranslationsType;
    breadcrumb: Array<{ title: string; url: string }>;
    blockStyleHandle: string;
    hasFilterInQuery: boolean;
    selectedLocale: string;
    backOrderValue: string;
    whichOneRender: {
        firstDivClassName: string;
        firstDivParams: { "data-layout": string } | {};
        secondDivClassName: string;
        showEmptyFiltersSection: boolean;
        showElement: "List" | "Empty"
    };
    getBuilderData: any;
    showBuilderData: boolean;
    currencies: Array<ICurrencies>;
    isMobile: boolean;
    recaptchaActive: string;
}

export interface IProductList {
    data: Array<IProduct>,
    dispatches: { setInitialMaxPrice: string, setInitialMinPrice: string }
    filters: Array<any>,
    links: any,
    max_price: number,
    meta: any,
    total: number,
    isInfiniteScroll: boolean,
    emptyCategory: boolean
    pageCount: number;
    getPages: Array<number>;
    showCatalog: boolean;
}

const CustomCategoryPage: FC<IShopPageCategoryProps> = (
    {
        brandList,
        productsList: {
            data,
            dispatches: {setInitialMaxPrice = "", setInitialMinPrice = ""} = {},
            isInfiniteScroll,
            pageCount,
            getPages,
            showCatalog
        },
        dbName,
        titleCategory,
        categoryDescription,
        selectedRate,
        translationsData,
        breadcrumb,
        blockStyleHandle,
        hasFilterInQuery,
        selectedLocale,
        backOrderValue,
        whichOneRender,
        getBuilderData,
        showBuilderData,
        domain,
        currencies,
        isMobile,
        recaptchaActive
    }
) => {
    const sidebarOpen = useSelector((state: IState) => state.sidebar.open) || false
    const router = useRouter();
    const options = optionsHandler(translationsData, router.query?.sort_by_price as string || "default")
    const [{
        code: selectedCurrCode = "",
        symbol: selectedCurrSymbol = "",
        exchange_rate: {rate: selectedCurrRate = "1"} = {},
    } = {}] = currencies
    const showBuilderComponents:(id: number) => ReactNode = (id) => {
        if (showBuilderData) {
            return <BuilderSection
                tab={id}
                getBuilderData={getBuilderData}
                domain={domain}
                dbName={dbName}
                translationsData={translationsData}
                selectedLocale={selectedLocale}
                currencies={currencies}
                backOrderValue={backOrderValue}
                selectedCurrItems={{selectedCurrCode, selectedCurrRate, selectedCurrSymbol}}
                isMobile={isMobile}
                recaptchaActive={recaptchaActive}
            />
        }
        return <Fragment />
    }

    const handleChange = async (value?: string) => {
        const updatedQuery: ParsedUrlQuery = router.query;
        delete updatedQuery?.page
        if (value === "default") {
            delete updatedQuery?.sort_by_price
            await router.push({query: updatedQuery}, {}, {scroll: false})
        } else {
            await router.push({query: {...updatedQuery, sort_by_price: value}})
        }
    };

    const ShowFilters = () => {
        if (hasFilterInQuery) return <Fragment />;
        const filterProps = {
            filters: brandList,
            maxPrice: setInitialMaxPrice,
            minPrice: setInitialMinPrice,
            selectedRate,
            translationsData,
        }
        if (isMobile) {
            if (sidebarOpen) {
                const CustomWidgetFiltersMobile = dynamic(() => import("./CustomWidgetFilters"), {
                    ssr: false,
                    loading: () => <Fragment />
                })
                return <CustomCategorySidebar translationsData={translationsData}>
                    <CustomWidgetFiltersMobile {...filterProps}/>
                </CustomCategorySidebar>;
            }
            return <CustomCategorySidebar translationsData={translationsData}>
                <Fragment />
            </CustomCategorySidebar>
        }
        return <CustomCategorySidebar translationsData={translationsData}>
            <CustomWidgetFilters {...filterProps}/>
        </CustomCategorySidebar>
    }

    const ShowDropDown = () => {
        if (data.length > 0) {
            return <div className={`${cls["products-list__body"]} ${cls.products_sort}`}>
                <CustomDropdown
                    item={options}
                    handleChange={handleChange}
                    translationsData={translationsData}
                />
            </div>
        }
        return <Fragment />
    }

    return (
        <div className="site_category_page">
            <BreadcrumbHeader breadcrumb={breadcrumb} dbName={dbName}/>
            <InfiniteContentLayout
                isInfinite={isInfiniteScroll}
                pageCount={pageCount}
                getPages={getPages}
                isMobile={isMobile}
            >
                {showBuilderComponents(1)}
                <div className="container">
                    <div className={cls.category_components_wrapper}>
                        <div className={cls[blockStyleHandle]}>
                            <div className={cls["category-title"]}><h1>{titleCategory}</h1></div>
                            <div
                                dangerouslySetInnerHTML={{__html: categoryDescription}}
                                className={cls["category-description"]}
                            />
                            {showBuilderComponents(2)}
                            {showCatalog ? <div className={cls["shop-layout"]}>
                                <ShowFilters/>
                                <div className={cls["shop-layout__content"]}>
                                    <ShowDropDown/>
                                    <CustomProductView
                                        productsList={data}
                                        dbName={dbName}
                                        selectedRate={selectedRate}
                                        selectedLocale={selectedLocale}
                                        backOrderValue={backOrderValue}
                                        translationsData={translationsData}
                                        whichOneRender={whichOneRender}
                                        isMobile={isMobile}
                                    />
                                </div>
                            </div> : null}
                        </div>
                    </div>
                </div>
                {showBuilderComponents(3)}
            </InfiniteContentLayout>
        </div>
    )
}

export default CustomCategoryPage;
